/**
 * Sleeping (vanilla Bedrock): lying pose geometry, the 101-tick timer and the
 * all-players rule. Pure except for `sleepHooks`, which the net layer fills.
 */
export const SLEEP_TICKS = 101;
/** Ticks the screen takes to go dark (vanilla fades over the same 100 ticks). */
export const SLEEP_FADE_TICKS = 100;
export const DEFAULT_SLEEP_PERCENTAGE = 100;

export interface SleepPlayer { sleeping: boolean; sleepTicks: number; spectator?: boolean }
export interface SleepDecision { skip: boolean; sleeping: number; total: number; needed: number }

/** Skip the night once enough players have been in bed for SLEEP_TICKS (vanilla playersSleepingPercentage). */
export function sleepDecision(players: ReadonlyArray<SleepPlayer>, percentage = DEFAULT_SLEEP_PERCENTAGE): SleepDecision {
  const list = players.filter((p) => !p.spectator);
  const total = list.length;
  const needed = Math.max(1, Math.ceil(total * Math.max(0, percentage) / 100));
  const sleeping = list.filter((p) => p.sleeping).length;
  const rested = list.filter((p) => p.sleeping && p.sleepTicks >= SLEEP_TICKS).length;
  return { skip: total > 0 && sleeping >= needed && rested >= needed && rested === sleeping, sleeping, total, needed };
}

/** "1/2 players sleeping" feedback line when others are still up. */
export function sleepStatusText(d: SleepDecision): string {
  return `${d.sleeping}/${d.total} players sleeping (sleeping through this night requires ${d.needed})`;
}

export interface LyingPose {
  /** feet-level body anchor (middle of the mattress, 9/16 up) */
  x: number; y: number; z: number;
  /** model yaw: forward points at the foot end, so the head rests on the pillow */
  yaw: number;
  /** first-person eye position on the pillow */
  eyeX: number; eyeY: number; eyeZ: number;
  /** standing spot beside the bed */
  standX: number; standY: number; standZ: number;
}

/** Bed whose head half is at (hx, hy, hz) and whose foot half lies along (-dx, -dz). */
export function lyingPose(hx: number, hy: number, hz: number, dx: number, dz: number, standSide: 1 | -1 = 1): LyingPose {
  const cx = hx + 0.5 - dx * 0.5, cz = hz + 0.5 - dz * 0.5;
  return {
    x: cx, y: hy + 9 / 16, z: cz, yaw: Math.atan2(dx, dz),
    eyeX: cx + dx * 0.55, eyeY: hy + 9 / 16 + 0.3, eyeZ: cz + dz * 0.55,
    standX: cx + -dz * standSide * 1.0, standY: hy, standZ: cz + dx * standSide * 1.0,
  };
}

/** Per-player sleep timer. */
export class SleepTimer {
  active = false;
  ticks = 0;
  start(): void { this.active = true; this.ticks = 0; }
  stop(): void { this.active = false; this.ticks = 0; }
  /** Advance one tick; true on the tick the player has slept long enough. */
  tick(): boolean { if (!this.active) return false; this.ticks++; return this.ticks === SLEEP_TICKS; }
  get fade(): number { return this.active ? Math.min(1, this.ticks / SLEEP_FADE_TICKS) : 0; }
}

export interface SleepWorld {
  setTime(t: number): void;
  wakeWeather(): void;
  resetRest(): void;
}
/** The time skip: dawn (next day's tick 0), clear weather, phantom insomnia reset. */
export function skipToDawn(w: SleepWorld): void { w.setTime(0); w.wakeWeather(); w.resetRest(); }

/**
 * Net-layer hook. The session must (a) set `remotePlayers` to return every OTHER
 * player's {sleeping, sleepTicks}, (b) set `onSkip` to broadcast the time skip, and
 * (c) read `localState()` to publish the local player's sleeping flag/ticks.
 */
export const sleepHooks: {
  remotePlayers: () => SleepPlayer[];
  onSkip: (() => void) | null;
  percentage: number;
  localState: () => SleepPlayer;
} = {
  remotePlayers: () => [],
  onSkip: null,
  percentage: DEFAULT_SLEEP_PERCENTAGE,
  localState: () => ({ sleeping: false, sleepTicks: 0 }),
};
